import type {MealDrinkKind,MfkMealLinkV1} from '../../../../contracts/meal-link-v1.ts';
import type {SyncedCombo,SyncedComboPool} from '../../runtime/published-config-projection.ts';
import {isPairedComboLine,standalonePriceForLine,type PairingProduct} from '../../runtime/riceball-promotion-pricing.ts';
import {projectDrinkSupplementChoices,type WorkspaceDrinkSupplementChoice} from './drink-supplement-choices.ts';
import type {WorkspaceProduct} from './OrderingCenterWorkspaces.tsx';
import {DEFAULT_PLATFORM_DRINK_RULE,type MainMealPolicyV1} from '../../../../contracts/meal-platform-config-v1.ts';
import {normalizeProductDisplayNames,productDisplayName,type ProductDisplayNames} from '../../../../contracts/product-display-names-v1.ts';
import {captureFixedRecipeSnapshot,type MfkFixedRecipeSnapshot,type FixedRecipeSnapshotProduct} from '../../../../contracts/order-line-composition-v1.ts';

export type MainMealRule=MainMealPolicyV1;
export interface MealCartLine{
  readonly id:string;readonly productId:string;readonly name:string;readonly qty:number;readonly unitMinor:number;
  readonly serviceMode:'takeaway'|'dine-in';readonly detail?:string;
  readonly displayNames?:ProductDisplayNames;
  readonly fixedRecipe?:MfkFixedRecipeSnapshot;
  readonly pairing?:{readonly role:'MAIN'|'SNACK';readonly groupLabel?:string;readonly sourceLineId?:string;readonly comboId?:string};
  readonly meal?:MfkMealLinkV1;
  readonly configuredCombo?:{readonly comboId:string};
  /** UI-only adjustment needed to undo an explicit no-drink selection. */
  readonly mealAdjustmentMinor?:number;
  readonly mealDrinkConfigurationAdjustmentMinor?:number;
  readonly mealConfiguredDetail?:string;
  readonly mealExtraPriceRequired?:boolean;
  readonly drinkTargetLineId?:string;
  /** #43-c3: 學生（落單時撳）— 只標記；學生價由結帳共用規則計。 */
  readonly studentDrink?:boolean;
}
/** Snapshot presentation only: identity, option facts and prices stay untouched. */
export function captureCartDisplayNames<T extends MealCartLine>(lines:readonly T[],products:readonly FixedRecipeSnapshotProduct[]):T[]{
  let changed=false;
  const result=lines.map(line=>{
    const product=products.find(product=>product.id===line.productId);
    const displayNames=line.displayNames??normalizeProductDisplayNames(product?.displayNames);
    const fixedRecipe=line.fixedRecipe??captureFixedRecipeSnapshot(product?.fixedRecipe,products);
    if(displayNames===line.displayNames&&fixedRecipe===line.fixedRecipe)return line;
    changed=true;return {...line,...(displayNames?{displayNames}:{}),...(fixedRecipe?{fixedRecipe}:{})};
  });
  return changed?result:lines as T[];
}
export function cartDisplayName(line:Pick<MealCartLine,'name'|'displayNames'>&{readonly variant?:{readonly receiptName?:string}}):string{
  return line.variant?.receiptName||productDisplayName(line,'pos');
}
export type MealDrinkChoice=WorkspaceDrinkSupplementChoice&{readonly drinkKind:MealDrinkKind;readonly standalonePriceMinor?:number};
export const MEAL_DRINK_PRODUCT_PREFIX='drink-supplement:meal:';
const detailParts=(detail?:string)=>String(detail??'').split(' · ').filter(part=>!part.startsWith('餐組：')&&!part.startsWith('配餐：')&&!part.startsWith('飲品待定')&&!part.startsWith('不飲'));
const withGroup=(line:MealCartLine,label:string)=>[...detailParts(line.detail),'餐組：'+label].join(' · ');
export function mealDrinkChoices(products:readonly WorkspaceProduct[],pools:readonly SyncedComboPool[],poolId:string):MealDrinkChoice[]{
  const pool=pools.find(row=>row.id===poolId&&row.kind==='ADDON'&&row.addonKind==='DRINK');
  if(!pool)return [];
  const kinds=new Map<string,MealDrinkKind>();
  const standalonePrices=new Map<string,number>();
  for(const group of pool.groups)for(const band of group.subPools)for(const choice of band.choices){
    const kind=choice.type==='NONE'?'NONE':band.mealDrinkKind;
    if(kind)kinds.set([pool.id,group.id,band.id,choice.id].join('::'),kind);
    if(choice.standalonePriceMinor!==undefined)standalonePrices.set([pool.id,group.id,band.id,choice.id].join('::'),choice.standalonePriceMinor);
  }
  return projectDrinkSupplementChoices(products,[pool]).flatMap(choice=>{
    const drinkKind=kinds.get(choice.id);
    return drinkKind?[{...choice,drinkKind,...(standalonePrices.has(choice.id)?{standalonePriceMinor:standalonePrices.get(choice.id)}:{})}]:[];
  });
}
export type MealProductCatalogs=Readonly<{takeaway:readonly PairingProduct[];'dine-in':readonly PairingProduct[]}>;
/** Resolve a pending meal against its own published recipe, never another meal's pool. */
export function mealDrinkChoicesForMeal(main:Pick<MealCartLine,'meal'|'pairing'>,products:readonly WorkspaceProduct[],pools:readonly SyncedComboPool[],combos:readonly SyncedCombo[],rule:MainMealRule|null):MealDrinkChoice[]{
  if(main.meal?.kind!=='RICEBALL_SNACK_DRINK'&&main.pairing?.role!=='MAIN')return mealDrinkChoices(products,pools,rule?.drinkPoolId??'');
  const combo=combos.find(row=>row.active&&row.id===main.pairing?.comboId);
  if(!combo)return [];
  return combo.addonPoolIds.flatMap(id=>mealDrinkChoices(products,pools,id));
}
/** Recover UI drafts made by the old quantity splitter. Keep every item and its adjacent drink. */
export function repairMealCartLineIds<T extends MealCartLine>(input:readonly T[]):T[]{
  if(new Set(input.map(line=>line.id)).size===input.length)return [...input];
  const reserved=new Set(input.map(line=>line.id)),seen=new Set<string>(),mainIds=new Map<string,string>();
  const unique=(id:string)=>{let next=id,n=1;while(seen.has(next)||next!==id&&reserved.has(next))next=id+'::recovered:'+n++;seen.add(next);reserved.add(next);return next;};
  return input.map(line=>{
    if(line.meal?.role==='MAIN'){
      const id=unique(line.id);mainIds.set(line.id,id);
      return {...line,id,meal:{...line.meal,groupId:id}} as T;
    }
    const groupId=line.meal?mainIds.get(line.meal.groupId)??line.meal.groupId:undefined;
    const id=unique(line.meal?.role==='DRINK'?groupId+'::meal-drink':line.id);
    return {...line,id,...(line.meal?{meal:{...line.meal,groupId:groupId!}}:{})} as T;
  });
}
export function normalizeMealCart<T extends MealCartLine>(input:readonly T[],rule:MainMealRule|null,choices:readonly MealDrinkChoice[],catalogs?:MealProductCatalogs):T[]{
  input=repairMealCartLineIds(input);
  if(!rule&&!input.some(line=>isPairedComboLine(line)))return [...input];
  const configuredGroups=new Set(input.filter(line=>line.configuredCombo).map(line=>line.pairing?.sourceLineId));
  const isConfigured=(line:MealCartLine)=>Boolean(line.pairing?.sourceLineId&&configuredGroups.has(line.pairing.sourceLineId));
  const byId=new Map(choices.map(choice=>[choice.id,choice]));
  const oldDrinks=new Map(input.filter(line=>line.meal?.role==='DRINK').map(line=>[line.meal!.groupId,line]));
  const output:T[]=[];
  let mealNumber=0;
  // A cup belongs to one meal. Split quantity groups before selection so mixed
  // choices and removing one cup never change all identical meals together.
  const reserved=new Set(input.map(line=>line.id)),origin=new Map<string,string>();
  const units=input.flatMap(line=>{
    if(!(!isConfigured(line)&&rule?.active&&rule.eligibleProductIds.includes(line.productId)&&line.meal?.role!=='DRINK'&&line.meal?.role!=='EXTRA'&&line.qty>1))return [line];
    let suffix=1;
    return Array.from({length:line.qty},(_,index)=>{
      let id=line.id;
      if(index){do{id=line.id+'::meal-unit:'+suffix++;}while(reserved.has(id)||reserved.has(id+'::meal-drink'));reserved.add(id);reserved.add(id+'::meal-drink');origin.set(id,line.id);}
      return {...line,id,qty:1};
    });
  });
  for(const line of units){
    if(isConfigured(line)){output.push(line);continue;}
    if(line.meal?.role==='DRINK')continue;
    if(line.meal?.role==='EXTRA'){
      const choice=byId.get(line.meal.drinkChoiceId);
      output.push(line.mealExtraPriceRequired&&choice?.standalonePriceMinor!==undefined?{...line,unitMinor:choice.standalonePriceMinor,mealExtraPriceRequired:false} as T:line);continue;
    }
    const pairedMain=isPairedComboLine(line)&&line.pairing?.role==='MAIN';
    const eligible=pairedMain||rule?.active&&rule.eligibleProductIds.includes(line.productId);
    if(!eligible){output.push(line);const old=oldDrinks.get(line.id);if(old)output.push(old);continue;}
    const kind=pairedMain?'RICEBALL_SNACK_DRINK' as const:'MAIN_DRINK' as const;
    mealNumber+=1;
    const groupLabel=pairedMain?'飯糰餐 '+(line.pairing?.groupLabel??mealNumber)+'組':'主食餐 '+mealNumber;
    const selected=line.meal?byId.get(line.meal.drinkChoiceId):undefined;
    if(!selected?.enabled){
      output.push({...line,unitMinor:line.unitMinor-(line.mealAdjustmentMinor??0),mealAdjustmentMinor:0,
        detail:withGroup(line,groupLabel)+' · 飲品待定 ×'+line.qty,
        meal:{groupId:line.id,groupLabel,kind,role:'MAIN',drinkChoiceId:'pending',drinkKind:'PENDING'}} as T);
      continue;
    }
    const previousAdjustment=line.meal?.role==='MAIN'?(line.mealAdjustmentMinor??0):0;
    const adjustment=selected.drinkKind==='NONE'?selected.adjustmentMinor:0;
    const base=catalogs&&!pairedMain?standalonePriceForLine(line,catalogs[line.serviceMode]):line.unitMinor-previousAdjustment;
    const main={...line,unitMinor:base+adjustment,mealAdjustmentMinor:adjustment,
      detail:withGroup(line,groupLabel)+(selected.drinkKind==='NONE'?' · 不飲':''),meal:{groupId:line.id,groupLabel,kind,role:'MAIN' as const,drinkChoiceId:selected.id,drinkKind:selected.drinkKind}} as T;
    output.push(main);
    if(selected.drinkKind==='NONE')continue;
    const prior=oldDrinks.get(line.id)??oldDrinks.get(origin.get(line.id)??'');
    const previous=prior?.meal?.drinkChoiceId===selected.id?prior:undefined;
    output.push({...previous,id:line.id+'::meal-drink',productId:selected.productId??MEAL_DRINK_PRODUCT_PREFIX+selected.id,
      name:selected.label,qty:line.qty,unitMinor:selected.adjustmentMinor+(previous?.mealDrinkConfigurationAdjustmentMinor??0),serviceMode:line.serviceMode,
      detail:['配餐：'+groupLabel+' · '+line.name,previous?.mealConfiguredDetail].filter(Boolean).join(' · '),meal:{...main.meal!,role:'DRINK'},mealAdjustmentMinor:undefined} as T);
  }
  return output;
}
export function chooseMealDrink<T extends MealCartLine>(cart:readonly T[],targetId:string,choiceId:string,rule:MainMealRule,choices:readonly MealDrinkChoice[],catalogs?:MealProductCatalogs):T[]{
  const target=cart.find(line=>line.id===targetId&&line.meal?.role==='MAIN');
  const choice=choices.find(row=>row.id===choiceId&&row.enabled);
  if(!target||!choice)throw Error('MEAL_DRINK_CHOICE_REQUIRED');
  return normalizeMealCart(cart.map(line=>line.id===targetId?{...line,meal:{...line.meal!,drinkChoiceId:choice.id,drinkKind:choice.drinkKind}} as T:line),rule,choices,catalogs);
}
export function unresolvedMealCount(
  cart:readonly MealCartLine[],
  rule:MainMealRule|null,
  choices?:readonly MealDrinkChoice[],
  choicesForLine?:(line:MealCartLine)=>readonly MealDrinkChoice[],
){
  const unpricedExtras=cart.filter(line=>line.mealExtraPriceRequired).length;
  return unpricedExtras+cart.filter(line=>{
    if(line.meal?.role==='DRINK'||line.meal?.role==='EXTRA')return false;
    if(!(rule?.active&&rule.eligibleProductIds.includes(line.productId))&&!(isPairedComboLine(line)&&line.pairing?.role==='MAIN'))return false;
    if(!line.meal||['PENDING','OMITTED'].includes(line.meal.drinkKind))return false;
    if(line.meal?.role!=='MAIN')return false;
    const availableChoices=choicesForLine?.(line)??choices;
    if(availableChoices&&!availableChoices.some(choice=>choice.enabled&&choice.id===line.meal!.drinkChoiceId))return true;
    if(line.meal.drinkKind==='NONE')return false;
    return !cart.some(drink=>drink.meal?.role==='DRINK'&&drink.meal.groupId===line.id&&drink.qty===line.qty);
  }).length;
}
export function pendingMealCount(cart:readonly MealCartLine[]){
  return cart.filter(line=>line.meal?.role==='MAIN'&&line.meal.drinkKind==='PENDING').reduce((sum,line)=>sum+line.qty,0);
}
export function clearMealDrink<T extends MealCartLine>(cart:readonly T[],targetId:string,rule:MainMealRule,choices:readonly MealDrinkChoice[],catalogs?:MealProductCatalogs){
  return normalizeMealCart(cart.map(line=>line.id===targetId&&line.meal?{...line,meal:{...line.meal,drinkChoiceId:'pending',drinkKind:'PENDING' as const}}:line),rule,choices,catalogs);
}
export function projectMealCartForChannel<T extends MealCartLine>(cart:readonly T[],channel:string,rule?:MainMealRule|null):{lines:T[];removedCups:number}{
  const policy=rule?.platformDrinkRules?.[channel]??(['keeta','foodpanda'].includes(channel)?DEFAULT_PLATFORM_DRINK_RULE:undefined);
  if(!policy)return {lines:[...cart],removedCups:0};
  let removedCups=0;
  const lines=cart.flatMap(line=>{
    const meal=line.meal;
    if(!meal||meal.kind!=='MAIN_DRINK'||meal.role==='EXTRA')return [line];
    if(policy.retainedChoiceIds.includes(meal.drinkChoiceId)||!policy.removedKinds.includes(meal.drinkKind)&&!policy.removedChoiceIds.includes(meal.drinkChoiceId))return [line];
    if(meal.role==='DRINK'){removedCups+=line.qty;return [];}
    return [{...line,unitMinor:line.unitMinor-(line.mealAdjustmentMinor??0),mealAdjustmentMinor:0,
      detail:withGroup(line,meal.groupLabel),meal:{...meal,drinkKind:'OMITTED',drinkChoiceId:'platform-omitted'}} as T];
  });
  return {lines,removedCups};
}
export function mealCartStateEqual(left:readonly MealCartLine[],right:readonly MealCartLine[]){
  return JSON.stringify(left)===JSON.stringify(right);
}

/** An existing meal needs a drink choice only until its selected cup is present. */
export function mealDrinkIsPending(cart:readonly MealCartLine[],main:MealCartLine,choices:readonly MealDrinkChoice[]):boolean{
  if(main.meal?.role!=='MAIN')return false;
  if(main.meal.drinkKind==='PENDING')return true;
  if(!choices.some(choice=>choice.enabled&&choice.id===main.meal!.drinkChoiceId))return true;
  if(main.meal.drinkKind==='NONE')return false;
  return !cart.some(drink=>drink.meal?.role==='DRINK'&&drink.meal.groupId===main.id&&drink.qty===main.qty);
}
